import React, { useState, useMemo, useCallback } from 'react';
import moment from 'moment';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import CircularProgress from '@mui/material/CircularProgress';
import ApproveBack from '../modal/ApproveBack';
import NotisReject from '../modal/NotisReject';

interface Row {
  userId: any;
  dateId: number;
  date: string;
  startTime: string;
  endTime: string;
  categoryid: any;
  description: string;
  allTime: string;
  tasks: any;
  userName: string;
  category: string;
  categoryTree: string;
  can_approve: boolean;
  superVisor: string;
}

interface Props {
  rows: Row[];
  search: string;
  updateRowsAfterStatusChange: () => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
  refreshUserHeder: () => void;
}

const GRADIENTS = [
  'linear-gradient(135deg,#1e40af,#60a5fa)',
  'linear-gradient(135deg,#065f46,#34d399)',
  'linear-gradient(135deg,#7c3aed,#a78bfa)',
  'linear-gradient(135deg,#9a3412,#fb923c)',
  'linear-gradient(135deg,#0e7490,#22d3ee)',
  'linear-gradient(135deg,#1d4ed8,#818cf8)',
  'linear-gradient(135deg,#7e1d5f,#e879f9)',
];
const grad = (i: number) => GRADIENTS[i % GRADIENTS.length];
const getInitials = (name: string) => {
  const p = name.trim().split(' ');
  return (p.length >= 2 ? p[0][0] + p[1][0] : name.slice(0, 2)).toUpperCase();
};
const fmtHours = (total: number) => {
  const h = Math.floor(total), m = Math.round((total - h) * 60);
  return `${h}:${m.toString().padStart(2, '0')}`;
};
const sumHours = (rws: Row[]) =>
  rws.reduce((a, r) => {
    const [h, m] = r.allTime.split(':').map(Number);
    return a + h + m / 60;
  }, 0);

const ApprovalsListView: React.FC<Props> = ({
  rows, search, updateRowsAfterStatusChange, loading, setLoading, refreshUserHeder,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [reportRows, setReportRows] = useState<Row[]>(rows);
  const [expandedUsers, setExpandedUsers]   = useState<Set<string>>(new Set());
  const [filterType, setFilterType]         = useState<'user' | 'category'>('user');
  const [selectedUser, setSelectedUser]     = useState('');
  const [categoryOrList, setCategoryOrList] = useState<string>(localStorage.getItem('approveListView') || 'category');
  const [sortDirection, setSortDirection]   = useState<'asc' | 'desc'>('asc');
  const [users, setUsers]                   = useState<{id:number;first_name:string;last_name:string}[]>([]);

  React.useEffect(() => {
    localStorage.setItem('approveListView', categoryOrList);
  }, [categoryOrList]);

  React.useEffect(() => {
    axios.get('users').then(({ data }) => setUsers(data)).catch(() => {});
  }, []);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [showReject, setShowReject] = useState(false);
  const [showBack, setShowBack] = useState(false);
  const [messege, setMessege] = useState('');
  const [rejectRow, setRejectRow] = useState<Row | null>(null);
  const [selReportId, setSelReportId] = useState<any>(null);
  const [selUserId, setSelUserId] = useState<any>(null);
  const [selWeek, setSelWeek] = useState<string>('');
  const [selFilterType] = useState<'user' | 'category'>('user');

  React.useEffect(() => { setReportRows(rows); }, [rows]);

  // Filter + group by user OR category
  const userGroups = useMemo(() => {
    const q = search.toLowerCase().trim();
    const g: Record<string, Row[]> = {};
    reportRows
      .filter(r => {
        if (q && !r.userName.toLowerCase().includes(q) && !r.categoryTree.toLowerCase().includes(q)) return false;
        return true;
      })
      .forEach(r => {
        const k = filterType === 'user' ? String(r.userId) : String(r.categoryid);
        (g[k] ??= []).push(r);
      });
    return Object.entries(g).sort(([, a], [, b]) => {
      const la = filterType === 'user' ? a[0].userName : a[0].categoryTree;
      const lb = filterType === 'user' ? b[0].userName : b[0].categoryTree;
      return la.localeCompare(lb);
    });
  }, [reportRows, search, filterType]);

  const getWeekGroups = (groupRows: Row[]) => {
    const g: Record<string, Row[]> = {};
    groupRows.forEach(r => {
      const k = `${moment(r.date).year()}-W${moment(r.date).isoWeek()}`;
      (g[k] ??= []).push(r);
    });
    return Object.entries(g).sort(([a], [b]) =>
      sortDirection === 'asc' ? a.localeCompare(b) : b.localeCompare(a)
    );
  };

  const toggleUser = (uid: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  // ── Actions ──────────────────────────────────────────────────
  const doApprove = useCallback(async (dateId: number) => {
    setProcessingId(dateId); setLoading(true);
    try {
      await axios.put('/time-reports/update-status', { reportsId: [dateId], status: 'fulfilled' });
      enqueueSnackbar('Report approved', { variant: 'success' });
      setReportRows(p => p.filter(r => r.dateId !== dateId));
    } catch { enqueueSnackbar('Error approving', { variant: 'error' }); }
    finally { setProcessingId(null); setLoading(false); }
  }, [enqueueSnackbar, setLoading]);

  const approveWeek = useCallback(async (groupKey: any, week: string) => {
    setLoading(true);
    const toApprove = reportRows.filter(r => {
      const k = filterType === 'user' ? String(r.userId) : String(r.categoryid);
      return k === String(groupKey) &&
        `${moment(r.date).year()}-W${moment(r.date).isoWeek()}` === week &&
        r.can_approve;
    });
    try {
      await Promise.all(toApprove.map(r =>
        axios.put('/time-reports/update-status', { reportsId: [r.dateId], status: 'fulfilled' })
      ));
      setReportRows(p => p.filter(r => !toApprove.map(x => x.dateId).includes(r.dateId)));
      enqueueSnackbar(`Approved ${toApprove.length} reports`, { variant: 'success' });
    } catch { enqueueSnackbar('Error approving week', { variant: 'error' }); }
    finally { setLoading(false); updateRowsAfterStatusChange(); }
  }, [reportRows, enqueueSnackbar, setLoading, updateRowsAfterStatusChange]);

  const BackReportsForWeek = useCallback(async (identifier: number, type: 'user' | 'category', week: string) => {
    setLoading(true);
    const toReturn = reportRows.filter(r =>
      String(r.userId) === String(identifier) &&
      `${moment(r.date).year()}-W${moment(r.date).isoWeek()}` === week
    );
    try {
      await axios.put('/time-reports/update-status', { reportsId: toReturn.map(r => r.dateId), status: 'new' });
      setReportRows(p => p.filter(r => !toReturn.map(x => x.dateId).includes(r.dateId)));
      enqueueSnackbar('Reports returned', { variant: 'success' });
    } catch { enqueueSnackbar('Error returning', { variant: 'error' }); }
    finally { setLoading(false); setShowBack(false); updateRowsAfterStatusChange(); }
  }, [reportRows, enqueueSnackbar, setLoading, updateRowsAfterStatusChange]);

  const doReject = useCallback(async (dateId: number) => {
    if (!messege.trim()) { enqueueSnackbar('Message is empty', { variant: 'error' }); return; }
    setProcessingId(dateId); setLoading(true);
    try {
      await axios.post('notice/add', {
        message: messege,
        source: `Reject: ${moment(rejectRow?.date).format('DD.MM.YYYY')} ${rejectRow?.categoryTree}, ${rejectRow?.allTime}`,
        sourceId: dateId, severity: 'warning', userId: selUserId,
      });
      await axios.put('/time-reports/update-status', { reportsId: [dateId], status: 'rejected', comments: messege });
      setReportRows(p => p.filter(r => r.dateId !== dateId));
      enqueueSnackbar('Report rejected', { variant: 'success' });
    } catch { enqueueSnackbar('Error rejecting', { variant: 'error' }); }
    finally { setProcessingId(null); setLoading(false); setShowReject(false); refreshUserHeder(); }
  }, [messege, rejectRow, selUserId, enqueueSnackbar, setLoading, refreshUserHeder]);

  if (!reportRows.length)
    return <div className="flex items-center justify-center py-[60px] text-[var(--text-color-calendar-week)] text-[14px]">{t('Empty')}</div>;

  return (
    <div style={{ padding: '16px' }}>

      {/* ── Filters — точно как в Approve ── */}
      <div className="flex items-center gap-[5px] flex-wrap" style={{ marginBottom: 14 }}>
        <p className="text-[12px] font-semibold whitespace-nowrap text-[var(--text-color-calendar-week)]">
          {t('SortBy')}:
        </p>
        {/* filterType: user | category */}
        <select
          value={filterType}
          onChange={e => { setFilterType(e.target.value as 'user' | 'category'); setSelectedUser(''); setExpandedUsers(new Set()); }}
          className="text-[13px] timesheet-edit-input px-[4px] py-[2px] rounded-[5px]">
          <option value="user">user</option>
          <option value="category">category</option>
        </select>
        {/* Вторичный фильтр — только в режиме user */}
        {filterType === 'user' && (
          <select
            value={categoryOrList}
            onChange={e => setCategoryOrList(e.target.value)}
            className="text-[13px] timesheet-edit-input px-[4px] py-[2px] rounded-[5px]">
            <option value="category">category</option>
            <option value="list">list</option>
          </select>
        )}
        {/* Week sort direction */}
        <select
          value={sortDirection}
          onChange={e => setSortDirection(e.target.value as 'asc' | 'desc')}
          className="text-[13px] timesheet-edit-input p-[4px] rounded-[5px]">
          <option value="asc">{t('week')} ⇈</option>
          <option value="desc">{t('week')} ⇊</option>
        </select>
      </div>

      {/* ── Accordion blocks ── */}
      {userGroups.map(([uid, userRows], idx) => {
        const isOpen = expandedUsers.has(uid);
        const weekGroups = getWeekGroups(userRows);
        const groupLabel = filterType === 'user' ? userRows[0].userName : userRows[0].categoryTree;
        const userName = groupLabel;
        const totalH = sumHours(userRows);
        const weekCount = weekGroups.length;

        return (
          <div key={uid} className="bg-[var(--bg-form)] rounded-[5px]"
            style={{ border: '1px solid var(--border-input)', marginBottom: 10, overflow: 'hidden' }}>

            {/* Block header */}
            <div
              className="flex items-center gap-[12px] px-[16px] py-[12px] cursor-pointer hover:bg-[var(--btn-user-form)]"
              style={{ userSelect: 'none', transition: 'background .12s' }}
              onClick={() => toggleUser(uid)}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: grad(idx), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: 'white', flexShrink: 0 }}>
                {getInitials(userName)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-color)', letterSpacing: '-.01em' }}>{userName}</div>
                <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>
                  {weekCount} {weekCount === 1 ? 'week' : 'weeks'} pending
                </div>
              </div>
              {/* Pills */}
              <div className="bg-[var(--btn-user-form)] rounded-[8px] text-[11px] font-semibold text-[var(--text-color)] px-[10px] py-[4px] flex-shrink-0"
                style={{ border: '1px solid var(--border-input)' }}>
                {userRows.length} reports
              </div>
              <div style={{ padding: '4px 10px', borderRadius: 8, background: 'rgba(5,150,105,.1)', color: '#059669', fontSize: 11, fontWeight: 700, flexShrink: 0, border: '1px solid rgba(5,150,105,.2)' }}>
                {fmtHours(totalH)}h
              </div>
              {/* Chevron */}
              <div className="bg-[var(--btn-user-form)] rounded-[8px] flex items-center justify-center flex-shrink-0"
                style={{ width: 28, height: 28, border: '1px solid var(--border-input)', fontSize: 10, color: 'var(--text-color-calendar-week)', transition: 'transform .25s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                ▶
              </div>
            </div>

            {/* Block body */}
            {isOpen && (
              <div style={{ borderTop: '1px solid var(--border-input)' }}>
                {weekGroups.map(([week, weekRows]) => {
                  const sorted = [...weekRows].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                  const weekNum = moment(weekRows[0].date).isoWeek();
                  const weekStart = moment(weekRows[0].date).startOf('isoWeek').format('D MMM');
                  const weekEnd = moment(weekRows[0].date).endOf('isoWeek').format('D MMM YYYY');

                  return (
                    <div key={week} style={{ padding: '0 16px 16px' }}>
                      {/* Week header */}
                      <div className="flex items-center justify-between flex-wrap gap-[10px] py-[12px]"
                        style={{ borderBottom: '1px solid var(--border-input)' }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)' }}>
                            Week {weekNum}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)' }}>
                            {weekStart} – {weekEnd}
                          </div>
                        </div>
                        <div className="flex items-center gap-[8px]">
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', paddingRight: 4 }}>
                            Total: <strong>{fmtHours(sumHours(sorted))}</strong>
                          </span>
                          <button type="button"
                            className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none rounded-[5px] cursor-pointer hover:bg-[var(--btn-user-hover)] text-[11px] font-semibold"
                            style={{ padding: '5px 12px' }}
                            disabled={loading}
                            onClick={() => { setSelReportId(uid); setSelWeek(week); setShowBack(true); }}>
                            ↩ {t('RemoveAllForWeek')}
                          </button>
                          <button type="button"
                            style={{ padding: '5px 12px', borderRadius: 5, border: 'none', background: 'rgba(5,150,105,.12)', color: '#059669', fontSize: 11, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}
                            disabled={loading}
                            onClick={() => approveWeek(uid, week)}>
                            ✓ {t('ApproveAllForWeek')}
                          </button>
                        </div>
                      </div>

                      {/* Table */}
                      <div style={{ borderRadius: 8, border: '1px solid var(--border-input)', overflow: 'hidden', marginTop: 10 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              {['Date', 'Time', 'Category', 'Task', 'Description', 'Hours', ''].map((h, i) => (
                                <th key={i} style={{
                                  padding: '8px 12px', textAlign: i >= 5 ? 'right' : 'left',
                                  fontSize: 10, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase',
                                  color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)',
                                  background: 'var(--btn-user-form)', whiteSpace: 'nowrap',
                                }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sorted.map((row, rIdx) => {
                              const busy = processingId === row.dateId;
                              const disabled = busy || !row.can_approve;
                              return (
                                <tr key={row.dateId}
                                  style={{ background: rIdx % 2 === 0 ? 'var(--bg-form)' : 'var(--btn-user-form)', opacity: busy ? .6 : 1 }}
                                  onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--bg-top)'}
                                  onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = rIdx % 2 === 0 ? 'var(--bg-form)' : 'var(--btn-user-form)'}>
                                  {/* Date */}
                                  <td style={{ padding: '9px 12px', fontSize: 12, fontWeight: 600, color: 'var(--text-color)', whiteSpace: 'nowrap', borderBottom: rIdx < sorted.length - 1 ? '1px solid var(--border-input)' : 'none' }}>
                                    {moment(row.date).format('D MMM, ddd')}
                                  </td>
                                  {/* Time */}
                                  <td style={{ padding: '9px 12px', fontSize: 12, fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--text-color)', whiteSpace: 'nowrap', borderBottom: rIdx < sorted.length - 1 ? '1px solid var(--border-input)' : 'none' }}>
                                    {moment.utc(row.startTime).format('HH:mm')} – {moment.utc(row.endTime).format('HH:mm')}
                                  </td>
                                  {/* Category */}
                                  <td style={{ padding: '9px 12px', borderBottom: rIdx < sorted.length - 1 ? '1px solid var(--border-input)' : 'none' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: 'rgba(37,99,235,.1)', color: '#2563eb', whiteSpace: 'nowrap' }}>
                                      {row.categoryTree}
                                    </span>
                                  </td>
                                  {/* Task */}
                                  <td style={{ padding: '9px 12px', fontSize: 11, color: 'var(--text-color-calendar-week)', borderBottom: rIdx < sorted.length - 1 ? '1px solid var(--border-input)' : 'none' }}>
                                    {row.tasks?.map((tk: any) => tk.name).join(', ')}
                                  </td>
                                  {/* Description */}
                                  <td style={{ padding: '9px 12px', fontSize: 11, color: 'var(--text-color-calendar-week)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderBottom: rIdx < sorted.length - 1 ? '1px solid var(--border-input)' : 'none' }}>
                                    {row.description}
                                  </td>
                                  {/* Hours */}
                                  <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: 'var(--text-color)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', borderBottom: rIdx < sorted.length - 1 ? '1px solid var(--border-input)' : 'none' }}>
                                    {row.allTime}
                                  </td>
                                  {/* Actions */}
                                  <td style={{ padding: '9px 12px', textAlign: 'right', whiteSpace: 'nowrap', borderBottom: rIdx < sorted.length - 1 ? '1px solid var(--border-input)' : 'none' }}>
                                    {busy ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                                        <button type="button" disabled={disabled}
                                          onClick={() => { setRejectRow(row); setSelReportId(row.dateId); setSelUserId(row.userId); setMessege(''); setShowReject(true); }}
                                          className="border-none rounded-[5px] cursor-pointer text-[10px] font-semibold"
                                          style={{ padding: '3px 9px', background: disabled ? 'var(--btn-user-btn)' : 'rgba(239,68,68,.1)', color: disabled ? 'var(--text-color-calendar-week)' : '#dc2626', cursor: disabled ? 'not-allowed' : 'pointer' }}>
                                          ↩ {t('Return')}
                                        </button>
                                        <button type="button" disabled={disabled}
                                          onClick={() => doApprove(row.dateId)}
                                          className="border-none rounded-[5px] cursor-pointer text-[10px] font-semibold"
                                          style={{ padding: '3px 9px', background: disabled ? 'var(--btn-user-btn)' : 'rgba(5,150,105,.12)', color: disabled ? 'var(--text-color-calendar-week)' : '#059669', cursor: disabled ? 'not-allowed' : 'pointer' }}>
                                          ✓ {t('Approve')}
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Footer */}
      <div className="flex items-center justify-between bg-[var(--bg-form)] rounded-[5px] px-[16px] py-[10px]"
        style={{ border: '1px solid var(--border-input)', marginTop: 16 }}>
        <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>
          {reportRows.length} reports · {userGroups.length} employees
        </span>
      </div>

      {/* Modals */}
      {showReject && (
        <NotisReject showReject={showReject} setReject={setShowReject} rejectReport={doReject} reportId={selReportId} setMessege={setMessege} />
      )}
      {showBack && (
        <ApproveBack showAprove={showBack} setShowAprove={setShowBack} BackReportsForWeek={BackReportsForWeek} reportId={Number(selReportId)} filterType={selFilterType} week={selWeek} />
      )}
    </div>
  );
};

export default ApprovalsListView;
